import { type ReactElement, useEffect, useRef } from 'react';
import {
  NeonButtonFooter,
  NeonModalDialog,
  NeonSelectField,
  NeonTextField,
  validateFields,
} from '@ps-refarch-ux/neon';
import { type SubjectAreaDialogProps } from '@/types';
import { MAX_SUBJECT_AREA_NAME_LENGTH } from '@/constants/neon-constants';
import { validateSubjectAreaName } from '@/utils/neon-utils';

export const SubjectAreaDialog = ({
  isOpen,
  modalId,
  title,
  subjectAreaName,
  selectedScedCode,
  scedOptions,
  isSubmitting,
  primaryText,
  primaryButtonId,
  buttonFooterId,
  cancelButtonId,
  nameFieldId,
  scedFieldId,
  isPrimaryDisabled = false,
  inlineErrorMessage,
  onSubjectAreaNameChange,
  onSelectedScedCodeChange,
  onPrimaryAction,
  onClose,
}: SubjectAreaDialogProps): ReactElement | null => {
  const dialogBodyRef = useRef<HTMLDivElement>(null);
  const hadDuplicateErrorRef = useRef(false);

  useEffect(() => {
    const hasDuplicateError = Boolean(inlineErrorMessage);
    const hasName = subjectAreaName.trim().length > 0;
    const hasScedSelection = Boolean(selectedScedCode);

    if (!isOpen) {
      hadDuplicateErrorRef.current = false;
      return;
    }

    // Revalidate both when duplicate first appears and when it clears, so Neon updates visible error state.
    if (hasName && hasScedSelection && (hasDuplicateError || hadDuplicateErrorRef.current)) {
      validateFields(dialogBodyRef.current, false);
    }

    hadDuplicateErrorRef.current = hasDuplicateError;
  }, [isOpen, inlineErrorMessage, selectedScedCode, subjectAreaName]);

  if (!isOpen) {
    return null;
  }
  const isSubjectAreaNameValid = validateSubjectAreaName(
    subjectAreaName,
    inlineErrorMessage
  ).isValid;

  return (
    <NeonModalDialog id={modalId} neonDialogHasClosed={onClose} dataSize="small">
      <div data-slot="dialog-header-title">{title}</div>
      <div
        ref={dialogBodyRef}
        style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
        data-slot="dialog-body"
      >
        <NeonTextField
          dataIsRequired
          modelValue={subjectAreaName}
          modelValueChange={(newValue: string | undefined): void => {
            onSubjectAreaNameChange(newValue || '');
          }}
          customValidationFunction={(value: string | undefined) =>
            validateSubjectAreaName(value, inlineErrorMessage)
          }
          id={nameFieldId}
          dataLabelText="Name"
          dataMaxLength={MAX_SUBJECT_AREA_NAME_LENGTH}
          dataShowCount={true}
        />
        <NeonSelectField
          modelValue={selectedScedCode}
          modelValueChange={(newValue: string | undefined): void => {
            onSelectedScedCodeChange(newValue);
          }}
          options={scedOptions}
          dataIsRequired
          id={scedFieldId}
          dataHelperText="School Courses for the Exchange of Data (SCED) subject code"
          dataLabelText="Map to"
          dataPlaceholderText={'Select a SCED subject code'}
        />
      </div>
      <div data-slot="dialog-button-footer">
        <NeonButtonFooter
          id={buttonFooterId}
          buttons={[
            {
              id: cancelButtonId,
              text: 'Cancel',
              type: 'borderless',
              onClick: (event): void => {
                event.target?.dispatchEvent(new Event('neonDialogClose', { bubbles: true }));
              },
            },
          ]}
          primaryButton={{
            id: primaryButtonId,
            text: primaryText,
            type: 'primary',
            disabled:
              !isSubjectAreaNameValid || !selectedScedCode || isPrimaryDisabled || isSubmitting,
            onClick: (): void => {
              onPrimaryAction();
            },
          }}
        />
      </div>
    </NeonModalDialog>
  );
};
